import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { environment } from '../environments/environment'
import { Router } from '@angular/router'
import { Injectable, inject } from '@angular/core'
import { TokenService } from './services/token.service'
import { PiaAuthService } from './auth/pia-auth.service'

@Injectable()
export class AxiosInterceptorService {
  private router = inject(Router)
  private tokenService = inject(TokenService)
  private piaAuth = inject(PiaAuthService)

  private readonly baseURL = environment.awsEndpointUrl.baseURL
  private excludedEndpoints: string[] = [
    '/account-services/auth/exchange',
    '/account-services/auth/refresh',
  ]
  private AuthorizationFailedError = 401
  private AccessForbiddenError = 403

  constructor() {
    this.setupInterceptor()
  }

  private setupInterceptor(): void {
    axios.interceptors.request.use(
      async (config) => {
        if (this.requiresToken(config)) {
          if (this.tokenService.isTokenExpired()) {
            const refreshed = await this.piaAuth.refresh()
            if (!refreshed) {
              await this.handleUnauthorizedResponse()
              return Promise.reject()
            }
          }
          const token: string | null = this.tokenService.getToken()
          if (token) {
            config.headers['Authorization'] = token
          }
        }
        return config
      },
      (error: AxiosError) => {
        return error
      }
    )

    axios.interceptors.response.use(
      (response) => {
        return response
      },
      async (error: AxiosError) => {
        const status = <number>error?.response?.status
        const original = error.config as
          | (InternalAxiosRequestConfig & { _retried?: boolean })
          | undefined

        // On a 401, the token may have been revoked/rotated server-side while it
        // still looked valid locally. Try one silent refresh + retry before logging out.
        if (
          status === this.AuthorizationFailedError &&
          original &&
          !original._retried &&
          this.requiresToken(original)
        ) {
          original._retried = true
          const refreshed = await this.piaAuth.refresh()
          if (refreshed) {
            const token = this.tokenService.getToken()
            if (token) original.headers['Authorization'] = token
            return axios(original)
          }
        }

        // We must redirect only in case of authorization error
        if ([this.AuthorizationFailedError, this.AccessForbiddenError].includes(status)) {
          await this.handleUnauthorizedResponse()
        }
        // Handling specific HTTP codes: TBD
        return Promise.reject(error)
      }
    )
  }

  private requiresToken(config: InternalAxiosRequestConfig): boolean {
    return !this.excludedEndpoints.some((endpoint) =>
      config.url?.startsWith(`${this.baseURL}${endpoint}`)
    )
  }

  private async handleUnauthorizedResponse() {
    this.tokenService.removeToken()
    await this.router.navigate(['/login'])
  }
}
